import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Chip, CircularProgress, makeStyles, TableContainer, InputAdornment, IconButton
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { getStockTransactions } from 'src/features/productSlice';
import moment from 'moment';

const PAGE_SIZE = 15;

const MOVEMENT_LABELS = {
  IMPORT: { label: 'Nhập kho', color: '#27ae60', bg: '#eafaf1' },
  EXPORT: { label: 'Xuất kho', color: '#e67e22', bg: '#fef9e7' },
  ADJUST: { label: 'Điều chỉnh', color: '#2980b9', bg: '#eaf4fb' },
  DAMAGED: { label: 'Hỏng/mất', color: '#e74c3c', bg: '#ffebee' },
  SALE: { label: 'Bán hàng', color: '#8e44ad', bg: '#f5eef8' },
  RETURN: { label: 'Hoàn kho', color: '#1abc9c', bg: '#e8f8f5' },
  RESERVE: { label: 'Giữ chỗ', color: '#f39c12', bg: '#fef5e7' },
  RELEASE: { label: 'Nhả giữ chỗ', color: '#7f8c8d', bg: '#f5f5f5' },
  TRANSFER_IN: { label: 'Chuyển đến', color: '#27ae60', bg: '#eafaf1' },
  TRANSFER_OUT: { label: 'Chuyển đi', color: '#e67e22', bg: '#fef9e7' }
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    '&.MuiPaper-elevation1': {
      boxShadow: 'none'
    }
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  filterSelect: {
    minWidth: 160,
    '& .MuiOutlinedInput-root': {
      height: 40,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      '& fieldset': {
        borderColor: '#E5E5E8',
        top: 0
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F',
        borderWidth: '1px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 14px',
      fontSize: '14px',
      height: 40,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center'
    },
    '& .MuiInputLabel-root': {
      fontSize: '13px',
      transform: 'translate(14px, 12px) scale(1)'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
      backgroundColor: '#ffffff',
      padding: '0 4px'
    }
  },
  tableWrapper: {
    '& .MuiTableCell-root': {
      borderBottom: '2px solid #F1F1F1 !important',
      padding: '8px 12px !important',
      fontSize: '14px'
    },
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#d2d9ddff',
      borderRadius: '8px'
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#bfc6caff' },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '8px'
    }
  }
}));

const StockTransactions = ({ products = [], agencies = [] }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { stockTransactions, transactionTotal, isLoadingTransactions } = useSelector(state => state.productSlice);

  const [page, setPage] = useState(1);
  const [filterProduct, setFilterProduct] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchTransactions = useCallback(() => {
    dispatch(getStockTransactions({
      page,
      page_size: PAGE_SIZE,
      product_id: filterProduct || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined
    }));
  }, [dispatch, page, filterProduct, fromDate, toDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getMovementChip = type => {
    const cfg = MOVEMENT_LABELS[type] || { label: type, color: '#666', bg: '#f5f5f5' };
    return (
      <span className="badge" style={{ backgroundColor: cfg.bg, color: cfg.color, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <Box>
      {/* Filters toolbar */}
      <Box className={classes.toolbar}>
        <TextField
          className={classes.filterSelect}
          size="small"
          select
          value={filterProduct}
          onChange={e => { setFilterProduct(e.target.value); setPage(1); }}
          SelectProps={{
            native: true
          }}
          variant="outlined"
          style={{ width: '220px' }}
        >
          <option value="">Tất cả sản phẩm</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{`[${p.code}] ${p.name}`}</option>
          ))}
        </TextField>

        <TextField
          label="Từ ngày"
          type="date"
          value={fromDate}
          onChange={e => { setFromDate(e.target.value); setPage(1); }}
          variant="outlined"
          size="small"
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: fromDate ? (
              <InputAdornment position="end" style={{ position: 'absolute', right: 28 }}>
                <IconButton size="small" onClick={() => { setFromDate(''); setPage(1); }} style={{ padding: 2 }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <TextField
          label="Đến ngày"
          type="date"
          value={toDate}
          onChange={e => { setToDate(e.target.value); setPage(1); }}
          variant="outlined"
          size="small"
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: toDate ? (
              <InputAdornment position="end" style={{ position: 'absolute', right: 28 }}>
                <IconButton size="small" onClick={() => { setToDate(''); setPage(1); }} style={{ padding: 2 }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} className={classes.root} style={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Box className={classes.tableWrapper}>
          {isLoadingTransactions ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '13%' }}>Thời gian</TableCell>
                  <TableCell style={{ width: '22%' }}>Sản phẩm</TableCell>
                  <TableCell style={{ width: '15%' }}>Đại lý</TableCell>
                  <TableCell style={{ width: '10%', textAlign: 'center' }}>SL thay đổi</TableCell>
                  <TableCell style={{ width: '10%', textAlign: 'center' }}>Số dư sau</TableCell>
                  <TableCell style={{ width: '15%' }}>Người thực hiện</TableCell>
                  <TableCell style={{ width: '15%' }}>Ghi chú</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">
                        <Box mb={1}>
                          <img src="/static/empty.png" alt="empty" style={{ height: 80, opacity: 0.6 }} />
                        </Box>
                        <Typography>
                          <h4>Không có biến động kho nào</h4>
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  stockTransactions.map((tx, idx) => {
                    const agencyName = tx.agency?.name || agencies.find(a => a.id === tx.agency_id)?.name || `#${tx.agency_id}`;
                    const productName = tx.product?.name || products.find(p => p.id === tx.product_id)?.name || `#${tx.product_id}`;
                    const isPositive = tx.quantity > 0;

                    return (
                      <TableRow hover key={tx.id || idx}>
                        <TableCell>
                          {tx.created_at ? moment(tx.created_at).format('DD/MM/YYYY HH:mm') : '—'}
                        </TableCell>
                        <TableCell>
                          {productName}
                          {tx.product?.code && (
                            <span style={{ display: 'block', fontSize: 12, color: '#888' }}>{tx.product.code}</span>
                          )}
                        </TableCell>
                        <TableCell>{agencyName}</TableCell>
                        <TableCell align="center">
                          {isPositive ? '+' : ''}{tx.quantity}
                        </TableCell>
                        <TableCell align="center">
                          {tx.balance_after ?? '—'}
                        </TableCell>
                        <TableCell>
                          {tx.user?.full_name || tx.user?.username || tx.creator?.full_name || tx.creator?.username || tx.created_by_user?.full_name || tx.created_by_user?.username || '—'}
                        </TableCell>
                        <TableCell>
                          <span style={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tx.note || '—'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </TableContainer>

      {stockTransactions.length > 0 && (
        <div className="mt-3 mr-4 float-right">
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px' }}>
              Tổng: {transactionTotal || 0}
            </Typography>
            <Pagination
              count={Math.ceil((transactionTotal || 0) / PAGE_SIZE)}
              color="primary"
              size="small"
              onChange={(_, p) => setPage(p)}
              page={page}
              siblingCount={1}
              boundaryCount={2}
              showFirstButton
              showLastButton
            />
          </Box>
        </div>
      )}
    </Box>
  );
};

export default StockTransactions;
